// TYPES ---------------------------------------------------------------- //

import type { Metric } from '../providers/MissingRefReportProvider';
import type { Moves_Bool_Exp } from '@gql/base-types';

interface WhereVariables {
  additionalFilters?: Moves_Bool_Exp[];
}

interface BuildWhereParams {
  usernames: string[];
  search?: string;
  metric: Metric;
}

// UTILITY ---------------------------------------------------------------- //

/**
 * Builds a usernames filter for the moves query based on the usernames array.
 * Filters moves where createdBy is in the provided usernames array.
 *
 * @param usernames - Array of usernames to filter by
 * @returns An array with a single filter condition, or empty array if no usernames
 */
const buildUsernamesFilter = (usernames: string[]): Moves_Bool_Exp[] => {
  if (!usernames || usernames.length === 0) {
    return [];
  }

  return [{ createdBy: { _in: usernames } }];
};

/**
 * Builds a search filter for the missing ref report query based on the search term.
 * Searches across multiple fields: id, lane (pickup/delivery name & address), vehicle fields, customer name.
 *
 * @param searchTerm - The search term to filter by
 * @returns An array with a single OR condition containing all searchable fields
 */
const buildSearchFilter = (searchTerm: string): Moves_Bool_Exp[] => {
  if (!searchTerm || !searchTerm.trim()) {
    return [];
  }

  const term = `%${searchTerm.trim()}%`;
  const trimmed = searchTerm.trim();

  // Check if search term is numeric (for ID search)
  const numericId = parseInt(trimmed, 10);
  const isNumeric = !isNaN(numericId) && numericId.toString() === trimmed;

  const searchConditions: Moves_Bool_Exp['_or'] = [
    { consumer_name: { _ilike: term } },
    { consumer_phone: { _ilike: term } },
    { customer: { name: { _ilike: term } } },
    { lane: { pickup: { name: { _ilike: term } } } },
    { lane: { pickup: { address: { _ilike: term } } } },
    { lane: { delivery: { name: { _ilike: term } } } },
    { lane: { delivery: { address: { _ilike: term } } } },
    { lane: { description: { _ilike: term } } },
    { vehicle_make: { _ilike: term } },
    { vehicle_model: { _ilike: term } },
    { vehicle_stock: { _ilike: term } },
    { vehicle_vin: { _ilike: term } },
    { vehicle_year: { _ilike: term } },
  ];

  // Add ID search if term is numeric
  if (isNumeric) {
    searchConditions.push({ id: { _eq: numericId } });
  }

  return [{ _or: searchConditions }];
};

/**
 * Builds GraphQL query variables for the missing ref report query based on the selected usernames, optional search term and metric.
 * The variables are designed to match the WHERE clauses in their aggregate counterparts
 * in getMovesMissingRefCounts.query.gql to ensure consistency between counts and displayed data.
 *
 * @param params - Object containing usernames, optional search term and metric
 * @returns GraphQL variables object with additional filters as boolean expressions
 */
export const buildMissingRefWhereVariables = ({ usernames, search, metric }: BuildWhereParams): WhereVariables => {
  const usernamesFilter = buildUsernamesFilter(usernames);
  const searchFilters = buildSearchFilter(search || '');

  switch (metric) {
    case 'all':
      // All moves - only search filters
      return { additionalFilters: [...usernamesFilter, ...searchFilters] };

    case 'upcoming':
      // Upcoming moves - not canceled, no status or dispatched
      return {
        additionalFilters: [
          { cancel_status: { _is_null: true } },
          { _or: [{ status: { _is_null: true } }, { status: { _eq: 'dispatched' } }] },
          ...usernamesFilter,
          ...searchFilters,
        ],
      };

    case 'inprogress':
      // In-progress moves - specific statuses
      return {
        additionalFilters: [
          { cancel_status: { _is_null: true } },
          {
            status: {
              _in: ['pickup started', 'pickup arrived', 'pickup successful', 'delivery started', 'delivery arrived'],
            },
          },
          ...usernamesFilter,
          ...searchFilters,
        ],
      };

    case 'completed':
      // Completed moves - delivery successful
      return {
        additionalFilters: [
          { cancel_status: { _is_null: true } },
          { status: { _eq: 'delivery successful' } },
          ...usernamesFilter,
          ...searchFilters,
        ],
      };

    case 'canceled':
      // Canceled moves - has cancel_status
      return {
        additionalFilters: [{ cancel_status: { _is_null: false } }, ...usernamesFilter, ...searchFilters],
      };

    default:
      return { additionalFilters: [...usernamesFilter, ...searchFilters] };
  }
};
